import { authService } from '@/services/auth.service';
import type { AuthResponse, AuthScope, OAuthProvider, SocialAuthIntent } from '@/types/auth';

declare global {
  interface Window {
    __florentinOAuthCompletedState?: string | null;
  }
}

const STORAGE_KEY = 'florentin.oauth.pending';
const COOKIE_KEY = 'florentin_oauth_pending';
const COOKIE_MAX_AGE_SECONDS = 5 * 60; // 5 minutes
const COMPLETED_KEY = 'florentin.oauth.completed';
const COMPLETED_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

interface PendingOAuthState extends SocialAuthIntent {
  scope: AuthScope;
  provider: OAuthProvider;
  state: string;
  redirectUrl: string;
  tenantSlug?: string;
  loginHint?: string;
  correlationId?: string;
  issuedAt: number;
}

interface CompletedOAuthState {
  scope: AuthScope;
  provider: OAuthProvider;
  response: AuthResponse;
  recordedAt: number;
}

interface StartRedirectOptions extends SocialAuthIntent {
  scope: AuthScope;
  provider: OAuthProvider;
  tenantSlug?: string;
  loginHint?: string;
  prompt?: string;
}

interface CompleteOptions {
  scope: AuthScope;
  provider: OAuthProvider;
  code: string;
  state?: string;
  idToken?: string;
  user?: unknown;
}

function resolveSessionStorage(): Storage | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

function writeCookie(value: PendingOAuthState): void {
  if (typeof document === 'undefined') {
    return;
  }

  const encoded = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${COOKIE_KEY}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function readCookie(): PendingOAuthState | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${COOKIE_KEY}=`));

  if (!cookie) {
    return null;
  }

  const raw = cookie.substring(COOKIE_KEY.length + 1);
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as PendingOAuthState;
    if (!isAuthScope(parsed.scope) || !isOAuthProvider(parsed.provider)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearCookie(): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
}

function readCompletedState(): CompletedOAuthState | null {
  const storage = resolveSessionStorage();
  let raw: string | null = null;

  if (storage) {
    raw = storage.getItem(COMPLETED_KEY);
  }

  if (!raw && typeof window !== 'undefined') {
    raw = window.__florentinOAuthCompletedState ?? null;
  }

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CompletedOAuthState;
    if (!isAuthScope(parsed.scope) || !isOAuthProvider(parsed.provider)) {
      return null;
    }
    if (!parsed.response || typeof parsed.recordedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.recordedAt > COMPLETED_MAX_AGE_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCompletedState(state: CompletedOAuthState): void {
  const payload = JSON.stringify(state);
  const storage = resolveSessionStorage();
  if (storage) {
    storage.setItem(COMPLETED_KEY, payload);
  }
  if (typeof window !== 'undefined') {
    window.__florentinOAuthCompletedState = payload;
  }
}

function clearCompletedState(): void {
  const storage = resolveSessionStorage();
  if (storage) {
    storage.removeItem(COMPLETED_KEY);
  }
  if (typeof window !== 'undefined') {
    delete window.__florentinOAuthCompletedState;
  }
}

function isAuthScope(value: unknown): value is AuthScope {
  return value === 'provider' || value === 'customer';
}

function isOAuthProvider(value: unknown): value is OAuthProvider {
  return value === 'google' || value === 'github' || value === 'apple' || value === 'azure';
}

export class SocialAuthPlugin {
  async startRedirect(options: StartRedirectOptions) {
    if (typeof window === 'undefined') {
      throw new Error('Social auth requires a browser environment');
    }

    const storage = resolveSessionStorage();
    clearCompletedState();

    const authorize = await authService.initiateOAuthAuthorization(options.provider, options.scope, {
      tenantSlug: options.tenantSlug,
      loginHint: options.loginHint,
      prompt: options.prompt,
      desiredRole: options.desiredRole,
      teamName: options.teamName,
      teamSeats: options.teamSeats,
      teamDescription: options.teamDescription,
    });

    const pending: PendingOAuthState = {
      scope: options.scope,
      provider: options.provider,
      state: authorize.state,
      redirectUrl: authorize.redirect_url,
      tenantSlug: options.tenantSlug,
      loginHint: options.loginHint,
      correlationId: authorize.correlation_id,
      issuedAt: Date.now(),
      desiredRole: options.desiredRole,
      teamName: options.teamName,
      teamSeats: options.teamSeats,
      teamDescription: options.teamDescription,
    };

    if (storage) {
      storage.setItem(STORAGE_KEY, JSON.stringify(pending));
    }
    writeCookie(pending);

    window.location.assign(authorize.redirect_url);

    return authorize;
  }

  async complete(options: CompleteOptions) {
    const pending = this.getPendingState();
    const scope = pending?.scope ?? options.scope;
    const provider = pending?.provider ?? options.provider;

    const response = await authService.exchangeOAuthCode(provider, scope, {
      code: options.code,
      state: options.state ?? pending?.state,
      tenantSlug: pending?.tenantSlug,
      idToken: options.idToken,
      user: options.user,
      correlationId: pending?.correlationId,
      desiredRole: pending?.desiredRole,
      teamName: pending?.teamName,
      teamSeats: pending?.teamSeats,
      teamDescription: pending?.teamDescription,
    });

    writeCompletedState({
      scope,
      provider,
      response,
      recordedAt: Date.now(),
    });
    this.clearPendingState();
    return response;
  }

  getPendingState(): PendingOAuthState | null {
    const storage = resolveSessionStorage();
    if (!storage) {
      return readCookie();
    }

    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      const cookieFallback = readCookie();
      if (cookieFallback) {
        storage.setItem(STORAGE_KEY, JSON.stringify(cookieFallback));
      }
      return cookieFallback;
    }

    try {
      const parsed = JSON.parse(raw) as PendingOAuthState;
      if (!isAuthScope(parsed.scope) || !isOAuthProvider(parsed.provider)) {
        return null;
      }
      return parsed;
    } catch {
      storage.removeItem(STORAGE_KEY);
      clearCookie();
      return null;
    }
  }

  clearPendingState(): void {
    const storage = resolveSessionStorage();
    if (storage) {
      storage.removeItem(STORAGE_KEY);
    }
    clearCookie();
  }

  consumeCompletedState(): CompletedOAuthState | null {
    const state = readCompletedState();
    if (!state) {
      return null;
    }
    clearCompletedState();
    return state;
  }
}

export const socialAuthPlugin = new SocialAuthPlugin();
